import { createContext } from 'react';
import { action, computed, observable, runInAction } from 'mobx';
import type { TFunction } from 'i18next';

import type { AiAssistMessage } from '@ole-ui/ole-ui-components';

import {
  streamLunaRecommendations,
  submitLunaRate,
  LunaRateValue,
  type LunaAssistantRelatedItem,
  type LunaAssistantStatus,
  type LunaAssistantTask,
  LunaRequestError,
} from 'services/LunaService';

const buildEmptyStateMessage = (
  status: LunaAssistantStatus | undefined,
  t: TFunction,
): AiAssistMessage => {
  if (status?.isUnavailable) {
    return {
      variant: 'warning',
      body: t(
        'The Learning Assistant is temporarily unavailable due to high system usage. Please try again later.',
      ),
    };
  }

  if (status?.didNotValidate) {
    return {
      variant: 'warning',
      body: t(
        'The selected text could not be validated. Please try selecting different text.',
      ),
    };
  }

  if (status?.isTooShort) {
    return {
      variant: 'info',
      body: t(
        'Your selection is too short. Select a longer passage to get recommendations.',
      ),
    };
  }

  if (status?.isTooLong) {
    return {
      variant: 'warning',
      body: t(
        'Your selection is too long. Select a shorter passage and try again.',
      ),
    };
  }

  if (status?.notEnoughContext) {
    return {
      variant: 'info',
      body: t(
        'Not enough context. Select more surrounding text and try again.',
      ),
    };
  }

  if (status?.noResultsFound) {
    return {
      variant: 'info',
      body: t(
        "We couldn't find anything related to this selection. Try a different passage.",
      ),
    };
  }

  return {
    variant: 'info',
    body: t(
      'The Learning Assistant is unavailable right now, try again later.',
    ),
  };
};

const buildUsageLimitMessage = (t: TFunction): AiAssistMessage => ({
  variant: 'warning',
  body: t(
    'AI usage limit temporarily reached. Please wait a moment before making another request.',
  ),
});

export type AIAssistantCourseContextGetters = {
  getUuid: () => string | null | undefined;
  getSlug: () => string | null | undefined;
};

class AIAssistantCourseStore {
  /**
   * Current text selection within the course content.
   * Used for mobile UX where we show a bottom-bar CTA instead of the floating quick menu.
   */
  @observable selectedText = '';

  /**
   * When the user requests recommendations before accepting the AI disclaimer,
   * we store the selection here and trigger the request after they click "I understand".
   */
  @observable pendingRecommendationText: string | null = null;

  /**
   * When the user requests a summary before accepting the AI disclaimer,
   * we store the selection here and trigger the request after they click "I understand".
   */
  @observable pendingSummaryText: string | null = null;

  /**
   * When the user requests elaboration before accepting the AI disclaimer,
   * we store the selection here and trigger the request after they click "I understand".
   */
  @observable pendingElaborationText: string | null = null;

  @observable isLoading = false;

  @observable currentTask: LunaAssistantTask = 'suggest';

  @observable message: AiAssistMessage | null = null;

  @observable outputHtml = '';

  @observable related: LunaAssistantRelatedItem[] = [];

  @observable lastInferenceRef: string | null = null;

  @observable rating: LunaRateValue = LunaRateValue.NoRating;

  @observable comment: string = '';

  /**
   * Stores the inference ref for which a comment was sent. This is how we know when to show the "feedback sent" message.
   */
  @observable commentSentForRef: string | null = null;

  private t: TFunction;

  private getters: AIAssistantCourseContextGetters;

  private lastRequestKey = '';

  private streamAbort: AbortController | null = null;

  private streamReqId = 0;

  private ratingAbort: AbortController | null = null;

  constructor(getters: AIAssistantCourseContextGetters, t?: TFunction) {
    this.getters = getters;
    // Stores are not React components, so they cannot call hooks.
    // Default to an identity function for tests / non-i18n usage.
    this.t = t ?? ((s: string) => s);
  }

  private abortInFlight() {
    // Invalidate callbacks first.
    this.streamReqId += 1;

    try {
      this.streamAbort?.abort();
    } catch (e) {
      // ignore
    }
    this.streamAbort = null;
  }

  @observable isOpen = false;

  @action setIsOpen = (isOpen: boolean) => {
    this.isOpen = isOpen;
  };

  @action setSelectedText = (text: string) => {
    this.selectedText = text;
  };

  @action setPendingRecommendationText = (text: string | null) => {
    this.pendingRecommendationText = text;
  };

  @action setPendingSummaryText = (text: string | null) => {
    this.pendingSummaryText = text;
  };

  @action setPendingElaborationText = (text: string | null) => {
    this.pendingElaborationText = text;
  };

  @action clear = () => {
    // Stop any in-flight stream so chunks don't repopulate after clearing.
    this.abortInFlight();
    this.isLoading = false;
    this.message = null;
    this.outputHtml = '';
    this.related = [];
    this.lastInferenceRef = null;
    this.rating = LunaRateValue.NoRating;
    this.comment = '';
    this.commentSentForRef = null;
    this.pendingRecommendationText = null;
    this.pendingSummaryText = null;
    this.pendingElaborationText = null;
  };

  private abortRating() {
    try {
      this.ratingAbort?.abort();
    } catch (e) {
      // ignore
    }
    this.ratingAbort = null;
  }

  /**
   * Abort any ongoing requests
   */
  @action dispose = () => {
    this.abortInFlight();
    this.abortRating();
  };

  @action setComment = (comment: string) => {
    this.comment = comment;
  };

  /**
   * True when a comment was sent for the current recommendation ref
   */
  @computed get commentSent(): boolean {
    return (
      this.commentSentForRef !== null &&
      this.commentSentForRef === this.lastInferenceRef
    );
  }

  /**
   * Submit rating for the last recommendation.
   * Component calls onRating(rating) for Like/Dislike, or onRating(rating, comment) when submitting the comment.
   * UI updates optimistically.
   * On API failure we revert to the previous rating, except when aborted on purpose (user clicked again).
   */
  @action submitRating = (
    buttonRating: LunaRateValue.ThumbsUp | LunaRateValue.ThumbsDown,
    comment?: string,
  ) => {
    if (!this.lastInferenceRef) return;

    // User resends the current rating with a comment.
    if (comment !== undefined) {
      this.abortRating(); // cancel any pending like/dislike request
      this.commentSentForRef = this.lastInferenceRef;

      submitLunaRate(
        {
          inference_ref: this.lastInferenceRef,
          rating: buttonRating,
          comment: comment.trim() || undefined,
        },
        {},
      ).catch(() => {
        runInAction(() => {
          this.commentSentForRef = null;
        });
      });
      return;
    }

    // Like/Dislike only: send the rating.
    this.commentSentForRef = null; // clear this so the user can add a new comment for the new rating
    this.comment = '';

    const previousRating = this.rating;
    const newRating =
      previousRating === buttonRating ? LunaRateValue.NoRating : buttonRating;
    this.rating = newRating;

    this.abortRating();

    const controller = new AbortController();
    this.ratingAbort = controller;

    submitLunaRate(
      { inference_ref: this.lastInferenceRef, rating: newRating },
      { signal: controller.signal },
    ).catch((err) => {
      if (err?.name === 'AbortError') return;
      runInAction(() => {
        this.rating = previousRating;
      });
    });
  };

  @action request = async (
    task: LunaAssistantTask,
    selectionText?: string,
    isFullPage?: boolean,
  ) => {
    if (!this.isOpen) {
      this.setIsOpen(true);
    }
    this.currentTask = task;

    const statement = (selectionText ?? '').trim();
    if (!statement) {
      this.outputHtml = '';
      this.related = [];
      this.lastInferenceRef = null;
      this.rating = LunaRateValue.NoRating;
      this.comment = '';
      this.commentSentForRef = null;
      this.message = null;
      this.isLoading = false;
      return;
    }

    const uuid = this.getters.getUuid?.();
    const slug = this.getters.getSlug?.();

    if (!uuid || !slug) {
      this.outputHtml = '';
      this.related = [];
      this.message = {
        variant: 'danger',
        body: 'Missing course context for Learning Assistant.',
      };
      this.isLoading = false;
      return;
    }

    const requestKey = `${task}::${slug}::${statement}`;

    // If the exact same request is already in-flight, keep it (avoid abort/restart thrash).
    if (
      this.isLoading &&
      this.lastRequestKey === requestKey &&
      this.streamAbort &&
      !this.streamAbort.signal.aborted
    ) {
      return;
    }

    this.lastRequestKey = requestKey;

    // Cancel any in-flight stream (new request should replace the previous output).
    this.abortInFlight();
    const reqId = this.streamReqId;
    const controller = new AbortController();
    this.streamAbort = controller;

    this.isLoading = true;
    this.message = null;
    this.outputHtml = '';
    this.related = [];
    this.rating = LunaRateValue.NoRating;
    this.comment = '';
    this.commentSentForRef = null;

    try {
      const finalChunk = await streamLunaRecommendations(
        {
          uuid,
          slug,
          task,
          statement,
          fullPage: isFullPage || false,
        },
        {
          signal: controller.signal,
          onChunk: (chunk) => {
            // Ignore chunks from stale requests.
            if (this.streamReqId !== reqId) return;

            runInAction(() => {
              if (chunk?.status?.isUnavailable) {
                // Backend indicates Luna is overloaded/unavailable; show a consistent warning.
                this.outputHtml = '';
                this.related = [];
                this.lastInferenceRef = null;
                this.message = buildEmptyStateMessage(chunk.status, this.t);

                // Stop streaming; nothing else to do.
                try {
                  controller.abort();
                } catch (e) {
                  // ignore
                }
                return;
              }

              if (typeof chunk.output === 'string') {
                this.outputHtml = chunk.output;
              }
              if (Array.isArray(chunk.related)) {
                this.related = chunk.related;
              }
              if (typeof chunk.ref === 'string') {
                this.lastInferenceRef = chunk.ref;
              }
            });
          },
        },
      );

      // Only mark as successful if the final snapshot has content.
      if (!controller.signal.aborted && this.streamReqId === reqId) {
        runInAction(() => {
          const finalOutput =
            typeof finalChunk?.output === 'string'
              ? finalChunk.output
              : this.outputHtml;
          const finalRelated = Array.isArray(finalChunk?.related)
            ? finalChunk.related
            : this.related;

          if (finalChunk?.status?.isUnavailable) {
            this.outputHtml = '';
            this.related = [];
            this.lastInferenceRef = null;
            this.message = buildEmptyStateMessage(finalChunk.status, this.t);
            return;
          }

          // Keep store aligned with the final snapshot.
          this.outputHtml = finalOutput || '';
          this.related = Array.isArray(finalRelated) ? finalRelated : [];

          const hasAnyContent = Boolean(
            this.outputHtml && this.outputHtml.trim(),
          );

          this.message = hasAnyContent
            ? null
            : buildEmptyStateMessage(finalChunk?.status, this.t);
        });
      }
    } catch (error: any) {
      // Ignore aborts (they happen when the user triggers a new request quickly).
      if (controller.signal.aborted) {
        return;
      }

      runInAction(() => {
        this.outputHtml = '';
        this.related = [];
        this.lastInferenceRef = null;
        this.commentSentForRef = null;
        const status: number | undefined =
          error instanceof LunaRequestError
            ? error.status
            : (error as any)?.status;

        let body: string =
          (typeof error?.message === 'string' && error.message) ||
          'Unable to fetch AI output.';

        const variant: AiAssistMessage['variant'] = 'danger';

        if (typeof status === 'number') {
          if (status === 404) {
            body =
              'Learning Assistant is unavailable right now (404). Please try again later.';
          } else if (status === 401 || status === 403) {
            body = 'You are not authorized to use the Learning Assistant.';
          } else if (status === 429) {
            this.message = buildUsageLimitMessage(this.t);
            return;
          } else if (status >= 500) {
            body =
              'Learning Assistant is temporarily unavailable. Please try again later.';
          } else {
            body = `Learning Assistant request failed (${status}).`;
          }
        }

        this.message = { variant, body };
      });
    } finally {
      if (this.streamReqId === reqId) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  };

  @action requestRecommendations = (
    selectionText?: string,
    isFullPage: boolean = false,
  ) => this.request('suggest', selectionText, isFullPage);

  @action requestSummary = (
    selectionText?: string,
    isFullPage: boolean = false,
  ) => this.request('summarize', selectionText, isFullPage);

  @action requestElaboration = (
    selectionText?: string,
    isFullPage: boolean = false,
  ) => this.request('expand', selectionText, isFullPage);
}

export default AIAssistantCourseStore;

export const aiAssistantContext = createContext<AIAssistantCourseStore | null>(
  null,
);
